import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useT } from "@/i18n";

const BOX_W = 288; // preview box width in px
const OUTPUT_W = 1024; // exported width in px
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/**
 * Crop dialog: drag to reposition, slide or scroll to zoom.
 * Returns a JPEG File matching the requested aspect ratio.
 */
export function ImageCropDialog({
  file,
  open,
  onOpenChange,
  onCropped,
  round = true,
  aspect = 1,
}: {
  file: File | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCropped: (file: File) => void | Promise<void>;
  round?: boolean;
  /** width / height of the crop area */
  aspect?: number;
}) {
  const { t } = useT();
  const [src, setSrc] = useState<string>("");
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const boxW = BOX_W;
  const boxH = Math.round(BOX_W / aspect);
  const outW = OUTPUT_W;
  const outH = Math.round(OUTPUT_W / aspect);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // cover the box at zoom = 1
  const base =
    natural.w && natural.h ? Math.max(boxW / natural.w, boxH / natural.h) : 1;
  const w = natural.w * base * zoom;
  const h = natural.h * base * zoom;

  const clamp = useCallback(
    (next: { x: number; y: number }) => {
      const maxX = Math.max(0, (w - boxW) / 2);
      const maxY = Math.max(0, (h - boxH) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [w, h, boxW, boxH],
  );

  useEffect(() => {
    setOffset((o) => clamp(o));
  }, [clamp]);

  // native non-passive wheel zoom
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
      const next = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, zoomRef.current * Math.exp(-dy * 0.0015)),
      );
      setZoom(next);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    setOffset(
      clamp({
        x: drag.current.ox + (e.clientX - drag.current.x),
        y: drag.current.oy + (e.clientY - drag.current.y),
      }),
    );
  }
  function onPointerUp() {
    drag.current = null;
  }

  async function apply() {
    if (!src || !file) return;
    setBusy(true);
    try {
      const img = new Image();
      img.src = src;
      await img.decode();
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas unavailable");
      const k = outW / boxW;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outW, outH);
      ctx.drawImage(
        img,
        (offset.x + (boxW - w) / 2) * k,
        (offset.y + (boxH - h) / 2) * k,
        w * k,
        h * k,
      );
      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Crop failed"))),
          "image/jpeg",
          0.92,
        ),
      );
      const cropped = new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", {
        type: "image/jpeg",
      });
      await onCropped(cropped);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("account.profile.cropTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            ref={boxRef}
            className="relative mx-auto touch-none overflow-hidden bg-muted"
            style={{ width: boxW, height: boxH, borderRadius: round ? "9999px" : "1rem" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {src && (
              <img
                src={src}
                alt=""
                draggable={false}
                onLoad={(e) =>
                  setNatural({
                    w: e.currentTarget.naturalWidth,
                    h: e.currentTarget.naturalHeight,
                  })
                }
                className="absolute left-1/2 top-1/2 max-w-none cursor-grab select-none active:cursor-grabbing"
                style={{
                  width: w || undefined,
                  height: h || undefined,
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                }}
              />
            )}
            <div
              className="pointer-events-none absolute inset-0 border-2 border-primary/60"
              style={{ borderRadius: round ? "9999px" : "1rem" }}
            />
          </div>

          <p className="text-center text-xs text-muted-foreground">
            {t("account.profile.cropHint")}
          </p>

          <div className="space-y-2">
            <Slider
              value={[zoom]}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              onValueChange={(v) => setZoom(v[0] ?? 1)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("account.profile.cropCancel")}
          </Button>
          <Button type="button" variant="hot" disabled={busy} onClick={() => void apply()}>
            {busy ? t("account.profile.uploading") : t("account.profile.cropSave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
